import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { cn } from '@/lib/cn';
import { useErMeta, useEncounterMutation } from './hooks';
import { recordTriage, suggestTriage } from './api';
import { LevelChoice, errorInfo } from './ui';
import type { Encounter, TriageInput, TriageSuggestion } from './types';
import VoiceFormFiller, { type VoiceField } from '@/components/VoiceFormFiller';
import {
  appendText, listField, numField, selectField, tempToC, textField, vBool, vList, vNum, vOneOf, vText, yesNoField, type Extracted,
} from '@/lib/voiceFill';

const VITALS: Array<{ key: keyof TriageInput; label: string; unit: string; step?: number }> = [
  { key: 'bp_systolic', label: 'BP sys', unit: 'mmHg' },
  { key: 'bp_diastolic', label: 'BP dia', unit: 'mmHg' },
  { key: 'pulse_rate', label: 'Pulse', unit: '/min' },
  { key: 'respiratory_rate', label: 'Resp. rate', unit: '/min' },
  { key: 'spo2', label: 'SpO₂', unit: '%' },
  { key: 'temperature', label: 'Temp', unit: '°C', step: 0.1 },
  { key: 'gcs', label: 'GCS', unit: '3–15' },
  { key: 'pain_score', label: 'Pain', unit: '0–10' },
  { key: 'grbs', label: 'GRBS', unit: 'mg/dL' },
];

// Spoken names for the vitals, so "sats 94", "sugar 180", "BP 90 by 60" land
// in the right box. Plausible-range checks keep a misheard number out of the
// suggestion engine (a GCS of 51 would otherwise score as a real reading).
const VITAL_VOICE: Array<{ key: keyof TriageInput; label: string; min: number; max: number }> = [
  { key: 'bp_systolic', label: 'Blood pressure — systolic (the upper/first number)', min: 30, max: 300 },
  { key: 'bp_diastolic', label: 'Blood pressure — diastolic (the lower/second number)', min: 10, max: 200 },
  { key: 'pulse_rate', label: 'Pulse / heart rate per minute', min: 10, max: 300 },
  { key: 'respiratory_rate', label: 'Respiratory rate per minute', min: 2, max: 80 },
  { key: 'spo2', label: 'SpO2 / oxygen saturation percent', min: 30, max: 100 },
  { key: 'temperature', label: 'Temperature (number as spoken)', min: 25, max: 115 },
  { key: 'gcs', label: 'GCS total out of 15', min: 3, max: 15 },
  { key: 'pain_score', label: 'Pain score 0-10', min: 0, max: 10 },
  { key: 'grbs', label: 'GRBS / blood sugar mg/dL', min: 10, max: 900 },
];
const AVPU = ['A', 'V', 'P', 'U'] as const;

/**
 * Triage / re-triage (design §7). The engine SUGGESTS a level from the red
 * flags and vitals (utils/erTriage.js, recomputed server-side on save); the
 * nurse CHOOSES. Choosing a different level is normal and needs a reason —
 * the server refuses a silent override. Every save appends to the triage
 * history (D5); nothing is overwritten.
 */
export default function TriagePanel({ encounter, open, onClose, onDone }: {
  encounter: Pick<Encounter, 'id' | 'er_number' | 'patient_name' | 'triaged_at' | 'chief_complaint' | 'is_mlc' | 'current_triage_level'>;
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
}) {
  const meta = useErMeta();
  const isRetriage = !!encounter.triaged_at;
  const [form, setForm] = useState<TriageInput>({ level_code: '' });
  const [suggestion, setSuggestion] = useState<TriageSuggestion | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const save = useEncounterMutation(encounter.id, (input: TriageInput) => recordTriage(encounter.id, input));

  useEffect(() => {
    if (open) {
      setForm({ level_code: '', chief_complaint: encounter.chief_complaint ?? '', red_flags: [], is_mlc: encounter.is_mlc });
      setSuggestion(null); setFields({}); setError(null);
    }
  }, [open, encounter.chief_complaint, encounter.is_mlc]);

  // Live suggestion — debounced, writes nothing.
  const probe = useMemo(() => {
    const p: Record<string, unknown> = { encounter_id: encounter.id, red_flags: form.red_flags ?? [] };
    for (const v of VITALS) if (form[v.key] !== undefined && form[v.key] !== '') p[v.key] = form[v.key];
    if (form.avpu) p.avpu = form.avpu;
    if (form.on_oxygen !== undefined) p.on_oxygen = form.on_oxygen;
    return JSON.stringify(p);
  }, [form, encounter.id]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      suggestTriage(JSON.parse(probe)).then(setSuggestion).catch(() => setSuggestion(null));
    }, 350);
    return () => clearTimeout(t);
  }, [probe, open]);

  const set = <K extends keyof TriageInput>(k: K, v: TriageInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const toggleFlag = (key: string) => setForm((f) => {
    const cur = new Set(f.red_flags ?? []);
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    return { ...f, red_flags: [...cur] };
  });

  // One dictation fills the complaint, vitals, AVPU, oxygen and red flags.
  // It never picks the triage LEVEL — the engine suggests and the nurse
  // decides, exactly as when typed — and it only ever ADDS red flags, never
  // clears one a nurse ticked.
  const redFlags = meta.data?.red_flags ?? [];
  const voiceFields: VoiceField[] = [
    textField('chief_complaint', 'Chief complaint'),
    ...VITAL_VOICE.map((v) => numField(v.key as string, v.label)),
    selectField('avpu', 'AVPU — A alert, V responds to voice, P responds to pain, U unresponsive', AVPU),
    yesNoField('on_oxygen', 'On supplemental oxygen'),
    ...(redFlags.length ? [listField('red_flags', 'Red flags — include one ONLY if the speaker explicitly stated it; never infer one from other findings (e.g. do not assume no loss of consciousness)', redFlags.map((f) => f.label))] : []),
    textField('notes', 'Any other triage notes'),
  ];
  const applyVoice = (x: Extracted) => setForm((f) => {
    const next: TriageInput = { ...f };
    const cc = vText(x, 'chief_complaint');
    if (cc) next.chief_complaint = appendText(f.chief_complaint, cc, '; ');
    for (const v of VITAL_VOICE) {
      let n = vNum(x, v.key as string);
      if (n === undefined) continue;
      if (v.key === 'temperature') n = tempToC(n);
      if (n >= v.min && n <= v.max) (next as unknown as Record<string, unknown>)[v.key as string] = n;
    }
    const avpu = vOneOf(x, 'avpu', AVPU);
    if (avpu) next.avpu = avpu;
    const o2 = vBool(x, 'on_oxygen');
    if (o2 !== undefined) next.on_oxygen = o2;
    const flagged = vList(x, 'red_flags', redFlags.map((r) => r.label))
      .map((label) => redFlags.find((r) => r.label === label)?.key).filter((k): k is string => !!k);
    if (flagged.length) next.red_flags = [...new Set([...(f.red_flags ?? []), ...flagged])];
    const notes = vText(x, 'notes');
    if (notes) next.notes = appendText(f.notes, notes, '\n');
    return next;
  });

  const levels = (meta.data?.levels ?? []).filter((l) => l.active && !l.mci_only);
  const suggested = suggestion?.suggestion.level_code ?? null;
  const overriding = !!form.level_code && !!suggested && suggested !== form.level_code;
  const ew = suggestion?.early_warning;

  const submit = async () => {
    setError(null); setFields({});
    if (!form.level_code) { setFields({ level_code: 'Choose a level' }); return; }
    const body: TriageInput = { ...form };
    for (const v of VITALS) if (body[v.key] === '') delete body[v.key];
    try {
      await save.mutateAsync(body);
      onDone?.();
      onClose();
    } catch (e) {
      const info = errorInfo(e);
      setError(info.message); setFields(info.fields);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="touch-form max-h-[92vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isRetriage ? 'Re-triage' : 'Triage'} — {encounter.patient_name}</DialogTitle>
          <DialogDescription>
            {encounter.er_number}{isRetriage ? ` · currently ${encounter.current_triage_level}` : ''} · the rules suggest, you decide.
          </DialogDescription>
        </DialogHeader>

        {error && <div className="rounded-lg bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{error}</div>}

        <VoiceFormFiller fields={voiceFields} onFields={applyVoice} label="🎙 Dictate triage"
          hint="RTA, head injury; BP 90 by 60, pulse 120, sats 92, RR 28, GCS 13, sugar 140; responds to voice; active bleeding" />

        <FormField label="Chief complaint">
          <Input value={form.chief_complaint ?? ''} onChange={(e) => set('chief_complaint', e.target.value)} autoFocus />
        </FormField>

        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {VITALS.map((v) => (
            <FormField key={v.key} label={`${v.label} (${v.unit})`} error={fields[v.key]}>
              <Input type="number" step={v.step ?? 1} inputMode="decimal"
                value={(form[v.key] as number | '' | undefined) ?? ''}
                onChange={(e) => set(v.key, (e.target.value === '' ? '' : Number(e.target.value)) as never)} />
            </FormField>
          ))}
          <FormField label="AVPU" error={fields.avpu}>
            <NativeSelect value={form.avpu ?? ''} onChange={(e) => set('avpu', e.target.value || undefined)}>
              <option value="">—</option>
              <option value="A">A — Alert</option>
              <option value="V">V — Voice</option>
              <option value="P">P — Pain</option>
              <option value="U">U — Unresponsive</option>
            </NativeSelect>
          </FormField>
        </div>
        <label className="flex items-center gap-2 text-[12px]">
          <Checkbox checked={form.on_oxygen === true} onCheckedChange={(c) => set('on_oxygen', c === true)} />
          On supplemental oxygen (scored by NEWS2)
        </label>

        <div>
          <div className="lbl">Red flags</div>
          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            {(meta.data?.red_flags ?? []).map((f) => (
              <label key={f.key} className={cn('flex items-center gap-2 rounded-md px-2 py-1 text-[12px]', (form.red_flags ?? []).includes(f.key) && 'bg-danger-soft')}>
                <Checkbox checked={(form.red_flags ?? []).includes(f.key)} onCheckedChange={() => toggleFlag(f.key)} />
                <span>{f.label}</span>
                {f.tier === 1 && <span className="ml-auto text-[10px] font-bold text-danger-ink">IMMEDIATE</span>}
              </label>
            ))}
          </div>
        </div>

        {ew && (
          <div className="rounded-lg border border-border bg-muted/60 p-3 text-[12px]">
            <div className="flex flex-wrap items-center gap-2 font-semibold">
              {ew.scale === 'NEWS2' && ew.news2
                ? <>NEWS2 {ew.news2.score}{!ew.news2.complete && ' (partial)'} · {ew.news2.risk} risk</>
                : <>Paediatric ranges{ew.band ? ` · ${ew.band}` : ''}</>}
            </div>
            {ew.alerts.length > 0 && (
              <ul className="mt-1 space-y-0.5">
                {ew.alerts.map((a, i) => <li key={i} className={a.severity === 'red' ? 'text-danger-ink' : 'text-warn-ink'}>• {a.message}</li>)}
              </ul>
            )}
            <div className="mt-1 text-[11px] text-ink-muted">{ew.clinical_basis}</div>
          </div>
        )}

        <div>
          <div className="lbl">Triage level{fields.level_code && <span className="ml-2 text-danger">{fields.level_code}</span>}</div>
          <div className="flex flex-wrap gap-2">
            {levels.map((l) => (
              <LevelChoice key={l.code} color={l.color} label={l.label} selected={form.level_code === l.code}
                suggested={suggested === l.code} onClick={() => set('level_code', l.code)} />
            ))}
          </div>
          {suggestion && suggestion.suggestion.reasons.length > 0 && (
            <div className="mt-1 text-[11px] text-ink-muted">
              Suggested because: {suggestion.suggestion.reasons.slice(0, 4).map((r) => r.reason).join(' · ')}
            </div>
          )}
        </div>

        {overriding && (
          <FormField label={`Why ${form.level_code} rather than the suggested ${suggested}?`} required error={fields.override_reason}>
            <Input value={form.override_reason ?? ''} onChange={(e) => set('override_reason', e.target.value)} />
          </FormField>
        )}

        <label className="flex items-center gap-2 text-[13px]">
          <Checkbox checked={!!form.is_mlc} disabled={encounter.is_mlc} onCheckedChange={(c) => set('is_mlc', c === true)} />
          Medico-legal case {encounter.is_mlc && '(already flagged)'}
        </label>

        <FormField label="Notes">
          <Textarea rows={2} value={form.notes ?? ''} onChange={(e) => set('notes', e.target.value)} />
        </FormField>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button loading={save.isPending} onClick={submit}>Save {isRetriage ? 're-triage' : 'triage'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
